import { useEffect } from "react"
import type { HighlightedText } from "@/components/desktop/wilsonViewContextRegistry"

let lastHighlight: HighlightedText | undefined

const elementOf = (node: Node | null): Element | null =>
    node instanceof Element ? node : (node?.parentElement ?? null)

const isEditable = (node: Node | null): boolean =>
    elementOf(node)?.closest("input, textarea, [contenteditable='true']") !=
    null

const onSelectionChange = (): void => {
    const selection = document.getSelection()
    // A load window's chat box is focused before anything is highlighted, so
    // focus only matters once the selection collapses: moving into a field to
    // ask keeps the highlight, a click elsewhere on the page drops it.
    if (!selection || selection.isCollapsed) {
        if (!isEditable(document.activeElement)) {
            lastHighlight = undefined
        }
        return
    }
    if (isEditable(selection.anchorNode)) {
        return
    }
    const text = selection.toString().trim()
    if (text.length === 0) {
        return
    }
    const windowId =
        elementOf(selection.anchorNode)
            ?.closest("[data-window-id]")
            ?.getAttribute("data-window-id") ?? undefined
    lastHighlight = { text, windowId }
}

export const takeHighlightedText = (): HighlightedText | undefined => {
    const highlight = lastHighlight
    lastHighlight = undefined
    return highlight
}

/**
 * Listens for highlights from now on. Adding the same listener twice is a
 * no-op, so every caller can start it; it stays on for the page's life.
 */
export const startHighlightTracking = (): void => {
    document.addEventListener("selectionchange", onSelectionChange)
    onSelectionChange()
}

export const useHighlightedTextTracker = (): void => {
    useEffect(startHighlightTracking, [])
}
